import { createContext, useContext } from "react";
import { OrganizationIntegrationWorkspacesResponse } from "@common/schemas/organization-integration.schema";

export type OrganizationIntegrationWorkspaceOption =
    OrganizationIntegrationWorkspacesResponse["workspaces"][number];

/**
 * An assignment picked in the table but not yet committed. The client is
 * always staged: it can't be persisted on its own (the API assigns by entity),
 * it is there to scope the entity options and to make the row's Entity cell
 * actionable. Whether the entity is staged too depends on the surface — see
 * `assignmentMode`.
 */
export interface PendingAssignment {
    workspaceId: string;
    entityId?: number;
}

export interface SetPendingEntityParams {
    accountIds: number[];
    workspaceId: string;
    entityId: number;
}

export interface AssignmentStaging {
    pendingByAccountId: Record<number, PendingAssignment>;
    setPendingClient: (accountId: number, workspaceId: string | null) => void;
    setPendingEntity: (params: SetPendingEntityParams) => void;
    clearPendingAccounts: (accountIds: number[]) => void;
}

/**
 * When an entity pick reaches the server.
 *
 * `commit` — the integrations table: each pick is its own request, because
 * rows are assigned one at a time against accounts that already exist in a
 * settled table.
 *
 * `stage` — the post-connect modal: picks accumulate and are sent in one batch
 * when the user confirms, so a half-finished pass over a fresh connection
 * leaves nothing behind.
 */
export type AssignmentMode = "commit" | "stage";

export interface OrganizationIntegrationsContextValue extends AssignmentStaging {
    organizationId: string;
    workspaces: OrganizationIntegrationWorkspaceOption[];
    assignmentMode: AssignmentMode;
    /**
     * Called once assignments persisted. Assigning re-buckets rows when the
     * table is grouped by client, which the table can fix but the modal that
     * triggered it cannot.
     */
    onAssignmentsCommitted: () => void;
}

export const OrganizationIntegrationsContext =
    createContext<OrganizationIntegrationsContextValue | null>(null);

export function useOrganizationIntegrations() {
    const context = useContext(OrganizationIntegrationsContext);
    if (!context) {
        throw new Error(
            "useOrganizationIntegrations must be used within an OrganizationIntegrationsContext.Provider",
        );
    }
    return context;
}
